/**
 * Inventory API Module
 *
 * Handles inventory management operations including adjustments,
 * stock queries, and adjustment history.
 */

import { apiClient } from './client';

// Adjustment reason options (must match worker)
export const AdjustmentReasons = [
  'damaged',
  'shrinkage',
  'found',
  'correction',
  'transfer_in',
  'transfer_out',
  'return_to_vendor',
  'other',
] as const;

export type AdjustmentReason = (typeof AdjustmentReasons)[number];

// Friendly labels for adjustment reasons
export const AdjustmentReasonLabels: Record<AdjustmentReason, string> = {
  damaged: 'Damaged',
  shrinkage: 'Shrinkage/Loss',
  found: 'Found',
  correction: 'Correction',
  transfer_in: 'Transfer In',
  transfer_out: 'Transfer Out',
  return_to_vendor: 'Return to Vendor',
  other: 'Other',
};

// Types
export interface InventoryItem {
  productId: number;
  variantId: number;
  sku: string;
  productName?: string;
  available: number;
  onHand?: number;
  committed?: number;
}

export interface InventoryListResponse {
  items: InventoryItem[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface AdjustmentInput {
  productId: number;
  variantId?: number;
  sku?: string;
  productName?: string;
  adjustment: number;
  reason: AdjustmentReason;
  notes?: string;
}

export interface AdjustInventoryRequest {
  locationId: number;
  items: AdjustmentInput[];
}

export interface AdjustmentResult {
  productId: number;
  variantId?: number;
  previousLevel: number;
  newLevel: number;
  adjustment: number;
  reason: AdjustmentReason;
}

export interface AdjustInventoryResponse {
  success: boolean;
  adjustments: AdjustmentResult[];
  message: string;
}

export interface AdjustmentHistoryEntry {
  id: string;
  productId: number;
  variantId?: number;
  sku?: string;
  productName?: string;
  adjustment: number;
  reason: AdjustmentReason;
  notes?: string;
  staffName: string;
  locationId: number;
  createdAt: string;
}

export interface AdjustmentHistoryResponse {
  adjustments: AdjustmentHistoryEntry[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface ProductSearchResult {
  productId: number;
  name: string;
  sku: string;
  imageUrl?: string;
  inventory: number;
  variants: {
    variantId: number;
    sku: string;
    optionValues?: string;
    inventory: number;
  }[];
}

export interface ProductSearchResponse {
  products: ProductSearchResult[];
}

/**
 * Inventory API
 */
export const inventoryApi = {
  /**
   * Get inventory levels at a location
   */
  async getInventory(params: {
    locationId: number;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<InventoryListResponse> {
    const searchParams = new URLSearchParams();
    searchParams.set('locationId', String(params.locationId));
    if (params.search) searchParams.set('search', params.search);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));

    return apiClient.request<InventoryListResponse>(`/inventory?${searchParams.toString()}`);
  },

  /**
   * Adjust inventory levels
   */
  async adjustInventory(request: AdjustInventoryRequest): Promise<AdjustInventoryResponse> {
    return apiClient.request<AdjustInventoryResponse>('/inventory/adjust', {
      method: 'POST',
      body: request,
    });
  },

  /**
   * Get adjustment history
   */
  async getAdjustmentHistory(params?: {
    locationId?: number;
    limit?: number;
    offset?: number;
  }): Promise<AdjustmentHistoryResponse> {
    const searchParams = new URLSearchParams();
    if (params?.locationId) searchParams.set('locationId', String(params.locationId));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const query = searchParams.toString();
    return apiClient.request<AdjustmentHistoryResponse>(`/inventory/history${query ? `?${query}` : ''}`);
  },

  /**
   * Search products for inventory adjustment
   */
  async searchProducts(params: {
    query: string;
    locationId: number;
    limit?: number;
  }): Promise<ProductSearchResponse> {
    const searchParams = new URLSearchParams();
    searchParams.set('query', params.query);
    searchParams.set('locationId', String(params.locationId));
    if (params.limit) searchParams.set('limit', String(params.limit));

    return apiClient.request<ProductSearchResponse>(`/inventory/search?${searchParams.toString()}`);
  },
};
